import React, { useState, useEffect } from 'react';
import {
  User, Mail, Phone, MapPin, Briefcase,
  GraduationCap,
  Globe,
  Download,
  X,
  ChevronLeft,
  ChevronRight,
  Filter,
  Search,
  Calendar,
  Send,
  Languages, Award, FileText, Eye, Building, AlertCircle, Loader,
  CheckCircle, Users, RefreshCw, Star,
  Bookmark, MessageSquare, ExternalLink, Clock,
  TrendingUp, Grid, List, Lock, Unlock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/Api';
import { useTranslation } from 'react-i18next';
import toast, { Toaster } from 'react-hot-toast';

const ProfileAvatar = ({ candidate, size = "small" }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    let objectUrl = null;

    const fetchImage = async () => {
      if (!candidate.profile_picture) {
        setImageUrl('');
        return;
      }

      // If it's a web URL, use it directly
      if (candidate.profile_picture.startsWith('http')) {
        setImageUrl(candidate.profile_picture);
        return;
      }

      // Otherwise, fetch via API
      try {
        setLoading(true);
        // Assuming profile_picture stores the filename or relative path
        // We need to extract folder and filename if it's a path like "folder/filename"
        // Or if it's just filename, we might need to know the folder.
        // Based on backend upload, it seems to be "folder/filename" or just "filename".
        // Let's assume it's "folder/filename" or we can try to guess.
        // The backend uploadProfilePicture saves `profile_pictures/${fileName}`.
        // So candidate.profile_picture might be `profile_pictures/user_123.jpg`.

        const pathParts = candidate.profile_picture.split('/');
        const filename = pathParts.pop();
        const folder = pathParts.length > 0 ? pathParts.join('/') : 'profile_pictures';

        const response = await api.get(`/files/${folder}/${filename}`, {
          responseType: 'blob',
        });

        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        objectUrl = window.URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      } catch (error) {
        console.error("Error loading profile picture:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchImage();

    return () => {
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [candidate.profile_picture]);

  const dimensions = size === "large" ? "w-20 h-20" : "w-8 h-8";
  const fontSize = size === "large" ? "text-2xl" : "text-xs";

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={candidate.first_name}
        className={`${dimensions} rounded-lg object-cover flex-shrink-0`}
      />
    );
  }

  return (
    <div className={`${dimensions} rounded-lg bg-primary-100 dark:bg-primary-950/50 flex items-center justify-center flex-shrink-0`}>
      <span className={`${fontSize} font-bold text-primary-600 dark:text-primary-400`}>
        {candidate.first_name?.charAt(0)}{candidate.last_name?.charAt(0)}
      </span>
    </div>
  );
};

const CVTheque = () => {
  const navigate = useNavigate();

  // State
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCVModal, setShowCVModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [wilayas, setWilayas] = useState([]);
  const [cities, setCities] = useState([]);
  const [languagesList, setLanguagesList] = useState([]);
  const [languageLevels, setLanguageLevels] = useState([]);
  const { i18n } = useTranslation();

  // Contact Modal State
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactMessage, setContactMessage] = useState('');

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCandidates: 0,
    limit: 20,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Filters
  const [filters, setFilters] = useState({
    location: '',
    gender: '',
    minAge: '',
    maxAge: '',
    isProfileComplete: 'true',
    sortBy: 'created_at',
    order: 'desc'
  });

  useEffect(() => {
    fetchCandidates();
    fetchStats();
    fetchReferenceData();
  }, [pagination.currentPage, filters]);

  const fetchReferenceData = async () => {
    try {
      const [wilayasRes, citiesRes, languagesRes, levelsRes] = await Promise.all([
        api.get('/data/wilayas'),
        api.get('/data/cities'),
        api.get('/data/languages'),
        api.get('/data/language-levels')
      ]);


      setWilayas(wilayasRes.data || []);
      setLanguagesList(languagesRes.data || []);
      setLanguageLevels(levelsRes.data || []);
      setCities(citiesRes.data || []);
    } catch (error) {
      console.error("Error fetching reference data:", error);
    }
  };

  const getWilayaName = (code) => {
    const wilaya = wilayas.find(w => w.id == code || w.code == code);
    if (!wilaya) return code;

    // Récupère la langue actuelle via i18n (i18next/react-i18next ou expo-localization)
    const currentLang = i18n.language || 'fr'; // 'fr', 'ar', 'en'

    // Retourne le nom correspondant à la langue
    return wilaya[`name_${currentLang}`] || wilaya.name_fr || code;
  };

  const getCityName = (code) => {
    const city = cities.find(c => c.id == code || c.code == code);
    if (!city) return code;

    // Récupère la langue actuelle via i18n (i18next/react-i18next ou expo-localization)
    const currentLang = i18n.language || 'fr'; // 'fr', 'ar', 'en'

    // Retourne le nom correspondant à la langue
    return city[`name_${currentLang}`] || city.name || code;
  };


  const getLanguageName = (code) => {
    const lang = languagesList.find(l => l.code == code);
    return lang ? lang[`name_${i18n.language}`] : code;
  };

  const getLanguageLevelName = (code) => {
    const level = languageLevels.find(l => l.code == code);
    return level ? level[`name_${i18n.language}`] : code;
  };

  const handleSendMessage = async () => {

    if (!contactMessage.trim() || !selectedCandidate) {
      console.error("Missing message or candidate", { contactMessage, selectedCandidate });
      return;
    }

    try {

      // 1. Create/Get Conversation
      const convResponse = await api.post('/chat/conversations', {
        participantId: selectedCandidate.user_id._id
      });
      const conversationId = convResponse.data.data._id;

      // 2. Send Message
      console.log("Sending message to conversation:", conversationId);
      await api.post('/chat/messages', {
        conversationId,
        content: contactMessage
      });

      // 3. Navigate to messages
      console.log("Navigating to messages");
      setShowContactModal(false);
      setContactMessage('');
      navigate('/dashboard/messages', {
        state: { conversationId }
      });
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showCVModal) {
        setShowCVModal(false);
      }
    };

    if (showCVModal) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [showCVModal]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit,
        ...filters
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await api.get(`/cvtheque?${params.toString()}`);

      setCandidates(response.data.data || []);
      setPagination(response.data.pagination || pagination);

      if (!selectedCandidate && response.data.data && response.data.data.length > 0) {
        setSelectedCandidate(response.data.data[0]);
      }

      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement de la CVthèque');
      console.error('Error fetching candidates:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/cvtheque/stats');
      setStats(response.data.stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short'
    });
  };

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleDownloadCV = async (folder, filename) => {
    try {
      const response = await api.get(`/files/${folder}/${filename}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Toasts.success("CV téléchargé avec succès");
    } catch (error) {
      console.error("Error downloading CV:", error);
      //  Toasts.error("Erreur lors du téléchargement du CV");
    }
  };

  const [cvBlobUrl, setCvBlobUrl] = useState(null);

  // Clean up blob URL when modal closes
  useEffect(() => {
    if (!showCVModal && cvBlobUrl) {
      window.URL.revokeObjectURL(cvBlobUrl);
      setCvBlobUrl(null);
    }
  }, [showCVModal, cvBlobUrl]);

  const handleViewCV = async () => {
    if (selectedCandidate?.cv_url && selectedCandidate.cv_url.length > 0) {
      try {
        const cvUrl = selectedCandidate.cv_url[0].url;
        const fileName = cvUrl.split('/').pop();
        const folder = cvUrl.split('/').slice(-2, -1)[0];

        const response = await api.get(`/files/${folder}/${fileName}`, {
          responseType: 'blob',
        });

        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        setCvBlobUrl(url);
        setShowCVModal(true);
      } catch (error) {
        console.error("Error viewing CV:", error);
      }
    }
  };

  const handleUnlockCandidate = async (candidateId) => {
    try {
      const response = await api.post(`/cvtheque/${candidateId}/unlock`);
      if (response.data.success) {
        // Update candidate in list
        const updatedCandidate = response.data.data;
        setCandidates(prev => prev.map(c => c._id === candidateId ? updatedCandidate : c));
        setSelectedCandidate(updatedCandidate);
      }
    } catch (error) {
      console.error("Error unlocking candidate:", error);
      toast.error(error.response?.data?.error || "Impossible de débloquer le candidat. Vérifiez votre quota.", {
        duration: 5000,
        position: 'top-center'
      });
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      location: '',
      gender: '',
      minAge: '',
      maxAge: '',
      isProfileComplete: 'true',
      sortBy: 'created_at',
      order: 'desc'
    });
    setSearchTerm('');
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchCandidates();
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') {
        handleSearch();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  if (loading && candidates.length === 0) {
    return (
      <div className="h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-10 w-10 text-primary-600 dark:text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error && candidates.length === 0) {
    return (
      <div className="h-screen bg-gray-50 dark:bg-gray-950 p-6 flex items-center justify-center">
        <div className="max-w-2xl w-full">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 dark:bg-red-950/30 rounded-2xl mb-4">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Erreur de chargement
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <button
              onClick={fetchCandidates}
              className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 dark:bg-gray-950">
      <Toaster />
      {/* Header - Compact */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Title + Stats */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-950/30 rounded-lg flex items-center justify-center">
                  <Users className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                </div>
                <div>
                  <h1 className="text-base font-bold text-gray-900 dark:text-white">CVthèque</h1>
                </div>
              </div>

              {stats && (
                <div className="hidden md:flex items-center gap-2 text-xs">
                  <div className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <span className="text-gray-700 dark:text-gray-300">{pagination.totalCandidates} candidats</span>
                  </div>
                  <div className="px-2 py-1 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
                    <span className="text-emerald-700 dark:text-emerald-400">{stats.completionRate}% complets</span>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Search + Actions */}
            <div className="flex items-center gap-2">
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, poste, compétences..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                />
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-colors ${showFilters
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
              >
                <Filter className="h-4 w-4" />
              </button>

              <button
                onClick={fetchCandidates}
                disabled={loading}
                className="p-2 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Filters Row */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  placeholder="Ville"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="w-32 px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <select
                  value={filters.gender}
                  onChange={(e) => handleFilterChange('gender', e.target.value)}
                  className="w-32 px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Genre</option>
                  <option value="m">Masculin</option>
                  <option value="f">Féminin</option>
                </select>
                <input
                  type="number"
                  placeholder="Âge min"
                  value={filters.minAge}
                  onChange={(e) => handleFilterChange('minAge', e.target.value)}
                  className="w-24 px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <input
                  type="number"
                  placeholder="Âge max"
                  value={filters.maxAge}
                  onChange={(e) => handleFilterChange('maxAge', e.target.value)}
                  className="w-24 px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="w-36 px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="created_at">Plus récent</option>
                  <option value="first_name">Prénom</option>
                  <option value="last_name">Nom</option>
                </select>
                <button
                  onClick={clearFilters}
                  className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5"
                >
                  <X className="h-3.5 w-3.5" />
                  Effacer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 3-Column Layout - Fixed Height */}
      <div className={`flex overflow-hidden ${showFilters ? 'h-[calc(100vh-180px)]' : 'h-[calc(100vh-120px)]'}`}>
        {/* Left Sidebar - Candidate List */}
        <div className="w-80 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col">
          {/* List Header */}
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between flex-shrink-0">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
              {candidates.length} résultats
            </span>
          </div>

          {/* Scrollable List */}
          <div className="flex-1 overflow-y-auto p-3">
            {loading && candidates.length === 0 ? (
              <div className="text-center py-8">
                <Loader className="h-6 w-6 text-primary-600 animate-spin mx-auto mb-2" />
                <p className="text-xs text-gray-500">Chargement...</p>
              </div>
            ) : candidates.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="h-8 w-8 text-gray-300 dark:text-gray-700 mx-auto mb-2" />
                <p className="text-xs text-gray-500">Aucun candidat</p>
              </div>
            ) : (
              <div className="space-y-2">
                {candidates.map((candidate) => (
                  <button
                    key={candidate._id}
                    onClick={() => setSelectedCandidate(candidate)}
                    className={`w-full p-3 rounded-lg transition-all text-left group ${selectedCandidate?._id === candidate._id
                      ? 'bg-primary-50 dark:bg-primary-950/30 border border-primary-200 dark:border-primary-800'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent'
                      }`}
                  >
                    <div className="flex items-center gap-2">
                      <ProfileAvatar candidate={candidate} size="small" />

                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate flex items-center gap-2">
                          {candidate.first_name} {candidate.last_name}
                          {candidate.isMasked && (
                            <div className="p-0.5 bg-gray-100 dark:bg-gray-800 rounded">
                              <Lock className="w-3 h-3 text-gray-400" />
                            </div>
                          )}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-500 truncate">
                          {candidate.experiences?.[0]?.title_id?.title || candidate.experiences?.[0]?.title || 'Pas d\'expérience'}
                        </p>
                      </div>

                      {candidate.cv_url && candidate.cv_url.length > 0 && (
                        <FileText className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Pagination - Fixed */}
          {pagination.totalPages > 1 && (
            <div className="p-3 border-t border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                <span>Page {pagination.currentPage}/{pagination.totalPages}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Préc.
                </button>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
                >
                  Suiv.
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Center - Main Content */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-950">
          {selectedCandidate ? (
            <div className="p-6 max-w-4xl mx-auto">
              {/* Profile Header */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 mb-4">
                <div className="flex items-start gap-4">
                  <ProfileAvatar candidate={selectedCandidate} size="large" />

                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                      {selectedCandidate.first_name} {selectedCandidate.last_name}
                    </h2>
                    {selectedCandidate.experiences?.[0] && (
                      <p className="text-gray-600 dark:text-gray-400 mb-3">
                        {selectedCandidate.experiences[0].title_id?.title || selectedCandidate.experiences[0].title} • {selectedCandidate.experiences[0].company}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {selectedCandidate.is_profile_complete && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium rounded-lg">
                          <CheckCircle className="h-3 w-3" />
                          Complet
                        </span>
                      )}
                      {selectedCandidate.birth_date && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-lg">
                          <Calendar className="h-3 w-3" />
                          {calculateAge(selectedCandidate.birth_date)} ans
                        </span>
                      )}
                      {selectedCandidate.address && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-lg">
                          <MapPin className="h-3 w-3" />
                          {getCityName(selectedCandidate.address.city)} {selectedCandidate.address.wilaya && `(${getWilayaName(parseInt(selectedCandidate.address.wilaya))})`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Bar */}
                {/* Contact Bar */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800">
                  {selectedCandidate.isMasked ? (
                    <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                          <Lock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">Profil Verrouillé</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Débloquez ce profil pour voir les coordonnées et télécharger le CV.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleUnlockCandidate(selectedCandidate._id)}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                      >
                        <Unlock className="w-4 h-4" />
                        Débloquer
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-3">
                      {selectedCandidate.user_id?.email && (
                        !selectedCandidate.user_id.email.includes('*') ? (
                          <a
                            href={`mailto:${selectedCandidate.user_id.email}`}
                            className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-500"
                          >
                            <Mail className="h-4 w-4" />
                            {selectedCandidate.user_id.email}
                          </a>
                        ) : (
                          <div className="inline-flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 cursor-not-allowed">
                            <Mail className="h-4 w-4" />
                            Email masqué
                          </div>
                        )
                      )}
                      {selectedCandidate.phone_number && (
                        !selectedCandidate.phone_number.includes('*') ? (
                          <a
                            href={`tel:${selectedCandidate.phone_number}`}
                            className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-500"
                          >
                            <Phone className="h-4 w-4" />
                            {selectedCandidate.phone_number}
                          </a>
                        ) : (
                          <div className="inline-flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 cursor-not-allowed">
                            <Phone className="h-4 w-4" />
                            Numéro masqué
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Details Sections */}
              <div className="space-y-4">
                {/* Experiences */}
                {selectedCandidate.experiences && selectedCandidate.experiences.length > 0 && (
                  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                      Expériences
                    </h3>
                    <div className="space-y-4">
                      {selectedCandidate.experiences.map((exp, index) => (
                        <div key={exp._id || index} className="flex gap-3">
                          <div className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-950/30 flex items-center justify-center flex-shrink-0">
                            <Building className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-semibold text-gray-900 dark:text-white">{exp.title_id?.title || exp.title}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{exp.company}</p>
                              </div>
                              {exp.is_current && (
                                <span className="px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium rounded">
                                  En cours
                                </span>
                              )}
                            </div>
                            {exp.description && (
                              <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">{exp.description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Education */}
                {selectedCandidate.education && selectedCandidate.education.length > 0 && (
                  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <GraduationCap className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                      Formation
                    </h3>
                    <div className="space-y-4">
                      {selectedCandidate.education.map((edu, index) => (
                        <div key={edu._id || index} className="flex gap-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center flex-shrink-0">
                            <GraduationCap className="h-4 w-4 text-emerald-600 dark:text-emerald-500" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 dark:text-white">{edu.degree}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{edu.institution}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-500">{edu.field}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Skills & Languages */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedCandidate.skills && selectedCandidate.skills.length > 0 && (
                    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <Award className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                        Compétences
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedCandidate.skills.map((skill, index) => (
                          <span
                            key={skill._id || index}
                            className="px-2 py-1 bg-primary-50 dark:bg-primary-950/30 text-primary-700 dark:text-primary-400 rounded-lg text-xs font-medium"
                          >
                            {skill.name_fr || skill.name || 'Compétence'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedCandidate.languages && selectedCandidate.languages.length > 0 && (
                    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <Languages className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                        Langues
                      </h3>
                      <div className="space-y-2">
                        {selectedCandidate.languages.map((lang, index) => (
                          <div key={lang._id || index} className="flex items-center justify-between">
                            <span className="text-sm text-gray-900 dark:text-white">{getLanguageName(lang.language)}</span>
                            <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded">
                              {getLanguageLevelName(lang.level)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <User className="h-16 w-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-500">Sélectionnez un candidat</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar - Quick Actions */}
        <div className="w-80 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto">
          {selectedCandidate ? (
            <div className="p-4 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Actions rapides</h3>

              {/* CV Actions */}
              {selectedCandidate.cv_url && selectedCandidate.cv_url.length > 0 ? (
                <div className="space-y-2">
                  <button
                    onClick={handleViewCV}
                    className="w-full px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <Eye className="h-4 w-4" />
                    Voir le CV
                  </button>
                  <button
                    onClick={
                      () => {
                        const fileName = selectedCandidate.cv_url[0].url.split('/').pop();
                        const folder = selectedCandidate.cv_url[0].url.split('/').slice(-2, -1)[0]; // Extract folder
                        handleDownloadCV(folder, fileName)

                      }
                    }
                    className="w-full px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <Download className="h-4 w-4" />
                    Télécharger CV
                  </button>
                </div>
              ) : (
                <div className="px-4 py-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl">
                  <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-2">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Aucun CV disponible
                  </p>
                </div>
              )}

              {/* Other Actions */}
              <div className="space-y-2">

                <button className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm">
                  <Calendar className="h-4 w-4" />
                  Planifier entretien
                </button>
                <button className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm">
                  <Bookmark className="h-4 w-4" />
                  Sauvegarder
                </button>
              </div>

              {/* Quick Info */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
                <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-3">Informations</h4>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Profil créé</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {formatDate(selectedCandidate.created_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Dernière MAJ</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {formatDate(selectedCandidate.updated_at)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center p-4">
              <p className="text-sm text-gray-500 text-center">Sélectionnez un candidat pour voir les actions</p>
            </div>
          )}
        </div>
      </div>

      {/* CV Modal */}
      {showCVModal && selectedCandidate?.cv_url && selectedCandidate.cv_url.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowCVModal(false)}
        >
          <div
            className="relative w-full max-w-6xl h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary-600 dark:text-primary-500" />
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    CV de {selectedCandidate.first_name} {selectedCandidate.last_name}
                  </h3>
                  <p className="text-xs text-gray-500">ESC pour fermer</p>
                </div>
              </div>
              <button
                onClick={() => setShowCVModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
            <div className="w-full h-[calc(100%-72px)]">
              <iframe
                src={`${cvBlobUrl}#view=FitH`}
                className="w-full h-full"
                title="CV"
              />
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default CVTheque;
